import { ChevronDown, Tag } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { gmailListLabels } from 'sprites-types/workflow/tools/gmailListLabels'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GmailListLabelsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(gmailListLabels, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const labels = Array.isArray(result) ? result : (result as any)?.labels || []

  if (labels.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Tag className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No labels found</div>
        </div>
      </div>
    )
  }

  const systemLabels = labels.filter(
    (label: any) =>
      label.type === 'system' || ['INBOX', 'SENT', 'DRAFT', 'SPAM', 'TRASH'].includes(label.id)
  )
  const userLabels = labels.filter(
    (label: any) =>
      label.type === 'user' || !['INBOX', 'SENT', 'DRAFT', 'SPAM', 'TRASH'].includes(label.id)
  )

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Tag className="h-5 w-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Gmail Labels</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {labels.length} labels ({systemLabels.length} system, {userLabels.length} user)
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="mx-8 space-y-4">
          {systemLabels.length > 0 && (
            <div>
              <div className="mb-2 text-xs font-semibold text-gray-500">SYSTEM LABELS</div>
              <div className="flex flex-wrap gap-2">
                {systemLabels.map((label: any) => (
                  <span
                    key={label.id}
                    className="rounded-full border border-blue-200 bg-blue-100 px-3 py-1 text-xs text-blue-800"
                  >
                    {label.name || label.id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {userLabels.length > 0 && (
            <div>
              <div className="mb-2 text-xs font-semibold text-gray-500">USER LABELS</div>
              <div className="flex flex-wrap gap-2">
                {userLabels.map((label: any) => (
                  <span
                    key={label.id}
                    className="rounded-full border border-green-200 bg-green-100 px-3 py-1 text-xs text-green-800"
                  >
                    {label.name || label.id}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function downloadGmailListLabels(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function gmailListLabelsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(gmailListLabels, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const labels = Array.isArray(result) ? result : (result as any)?.labels || []

  if (labels.length === 0) {
    return `## Gmail Labels

No labels found.

---
`
  }

  const systemLabels = labels.filter(
    (label: any) =>
      label.type === 'system' || ['INBOX', 'SENT', 'DRAFT', 'SPAM', 'TRASH'].includes(label.id)
  )
  const userLabels = labels.filter(
    (label: any) =>
      label.type === 'user' || !['INBOX', 'SENT', 'DRAFT', 'SPAM', 'TRASH'].includes(label.id)
  )

  let markdown = `## Gmail Labels (${labels.length} total)\n\n`

  if (systemLabels.length > 0) {
    markdown += `### System Labels (${systemLabels.length})\n`
    markdown += systemLabels.map((label: any) => `- ${label.name || label.id}`).join('\n')
    markdown += '\n\n'
  }

  if (userLabels.length > 0) {
    markdown += `### User Labels (${userLabels.length})\n`
    markdown += userLabels.map((label: any) => `- ${label.name || label.id}`).join('\n')
    markdown += '\n\n'
  }

  return markdown + '---\n'
}

export const GmailListLabelsDisplay = createWorkflowToolComponent(
  GmailListLabelsDisplayComponent,
  downloadGmailListLabels,
  gmailListLabelsToMarkdown
)
